<template>
  <section v-if="submitPolicyInProcess">
    <h1>{{ submitPolicyInProcessHeader }}</h1>
    <p v-html="ErrorStrings.SUBMIT_POLICY_IN_PROCESS_ERROR.SUBHEADER"></p>
  </section>
  <PremiumImpactInformation v-else :showPremiumInformation="false" :refData="refData">
    <!-- title slot: header -->
    <template #header>
      <h1>{{ $route.meta.title }}</h1>
    </template>
    <template #headerInfoText>
      <div v-html="refData.premiumImpactInformationText.informationText" />
    </template>
    <template #tableHeader>
      <h2>Your updated product</h2>
    </template>
    <template #productPremiumContent>
      <ProductSummaryCard>
        <template #left>
          <h3>{{ productName }}</h3>
          <div v-html="refData && refData.updatedProductInformationText"></div>
        </template>
        <template #right>
          <h4 class="header">Your premium</h4>
          <h2 class="premium-amount-frequency">{{ premiumAmount }}</h2>
          <h6 class="premium-amount-frequency">/ {{ premiumFrequency }}</h6>
        </template>
      </ProductSummaryCard>
    </template>
    <template #premiumAdjustmentTimeline>
      <ApiLoading
        class="api-loading"
        v-if="apiStatus.premiumAdjustmentTimeline.loading || apiStatus.premiumAdjustmentTimeline.fail"
        :apiError="apiStatus.premiumAdjustmentTimeline.fail"
      />
      <PremiumAdjustmentTimeline
        v-if="!apiStatus.premiumAdjustmentTimeline.loading && !apiStatus.premiumAdjustmentTimeline.fail"
        :refData="refData"
        :premiumTimelineData="premiumTimelineData"
        @handlePayNowBtnClick="handlePayNowBtnClick"
      />
    </template>
  </PremiumImpactInformation>
</template>

<script>
import { getCurrentDate, convertToCurrencyString } from '@general/js/utils';
import { mapPaymentFrequencyFromRecurringToSingleton } from '@/utils';
import { PremiumAdjustmentTimeline, ApiLoading, PremiumImpactInformation, ProductSummaryCard } from '@/components';
import { MY_COVER_MAKE_A_PAYMENT } from '@/router/routeNames';
import { setNavigationState } from '@/router/navigationState';
import PremiumImpactServices from '@/services/PremiumImpact';
import { ErrorStrings } from '@general/js/constants';
import { generateSubmitPolicyInProcessHeader } from '@/utils/DisplayUtils';
import ChangeExcessMixins from './ChangeExcessMixins';

export default {
  name: 'ChangeExcessPremiumImpact',

  mixins: [ChangeExcessMixins],

  components: {
    ApiLoading,
    PremiumImpactInformation,
    PremiumAdjustmentTimeline,
    ProductSummaryCard,
  },

  props: {
    /**
     * The new premium amount to display.
     * This prop is only required if `submitPolicyInProcess` is false.
     */
    newPremium: {
      type: Number,
    },
    /**
     * Reference data for premium impact information.
     * This prop is only required if `submitPolicyInProcess` is false.
     */
    refData: {
      type: Object,
    },
    /**
     * Flow name to be used in generating submit policy in process header.
     * This prop is only required if `submitPolicyInProcess` is true.
     */
    flowName: {
      type: String,
      required: false,
    },
  },

  data() {
    return {
      apiStatus: {
        premiumAdjustmentTimeline: { loading: false, fail: false },
      },
      premiumTimelineData: [
        {
          paymentAmount: null,
          label: 'Difference to amount paid',
        },
        {
          paymentAmount: null,
          label: 'Next payment',
          date: null,
        },
        {
          paymentAmount: null,
          label: 'Payment onwards',
          date: null,
        },
      ],
      premiumAdjustmentPrices: null,
      productName: null,
      ErrorStrings,
    };
  },

  created() {
    this.getPremiumAdjustmentPrices();
  },

  beforeMount() {
    this.productName = this.membershipInfo.productName;
  },

  computed: {
    premiumAmount() {
      let newPremiumAmount;
      if (typeof this.newPremium === 'number') {
        newPremiumAmount = convertToCurrencyString(this.newPremium);
      } else {
        newPremiumAmount = `$${this.newPremium}`;
      }
      return newPremiumAmount;
    },

    premiumFrequency() {
      return mapPaymentFrequencyFromRecurringToSingleton(this.newPremiumAndFrequency.frequency, false);
    },

    submitPolicyInProcessHeader() {
      return generateSubmitPolicyInProcessHeader(this.flowName, this.submitPolicyInProcess);
    },
  },

  methods: {
    async getPremiumAdjustmentPrices() {
      this.apiStatus.premiumAdjustmentTimeline.loading = true;
      this.apiStatus.premiumAdjustmentTimeline.fail = false;

      try {
        const currentMemberNumber = this.$route?.params?.policyNumber;
        this.premiumAdjustmentPrices = await PremiumImpactServices.getPremiumAdjustmentPrices(currentMemberNumber, true, getCurrentDate());

        this.premiumTimelineData[0].paymentAmount = this.premiumAdjustmentPrices.prorataPrice;

        this.premiumTimelineData[1].paymentAmount = this.premiumAdjustmentPrices.standardPremiumPaymentAmount;
        this.premiumTimelineData[2].paymentAmount = this.premiumAdjustmentPrices.standardPremiumPaymentAmount;

        this.premiumTimelineData[1].date = this.premiumAdjustmentPrices.nextContributionDate;
        this.premiumTimelineData[2].date = this.premiumAdjustmentPrices.futureContributionDate;
      } catch (err) {
        logger.error(err);
        this.apiStatus.premiumAdjustmentTimeline.fail = true;
      } finally {
        this.apiStatus.premiumAdjustmentTimeline.loading = false;
      }
    },

    handlePayNowBtnClick(amt) {
      setNavigationState({ paymentAmount: amt });
      this.$router.push({ name: MY_COVER_MAKE_A_PAYMENT });
    },
  },
};
</script>

<style lang="scss" scoped>
.premium-amount-frequency {
  color: $CRIMSON;
  display: inline;
}
</style>
